/**
 * FE mirror of the sitewide assistant's server-side route manifest.
 *
 * Source of truth: `services/console/ai/src/routes/routeManifest.ts`.
 * Lock: `apps/console/src/routes/__tests__/route-manifest.parity.spec.ts`,
 * which imports the backend file by deep relative path and asserts entry-
 * for-entry equality with `FE_ROUTE_MANIFEST` below.
 *
 * `apps/console` deliberately does not depend on `@gisify/utilities` (or
 * zod) — keeping those out of the browser bundle is intentional — so this
 * file is a verbatim COPY, not an import, of the backend manifest. Per the
 * backend file's own header comment, it has zero imports of its own, which
 * is exactly what makes the parity spec's deep relative import into the
 * backend safe (same pattern as
 * `apps/console/src/components/organisms/AIUsage/__tests__/elementLabel.parity.spec.ts`).
 *
 * Do NOT import zod, `@gisify/utilities`, or backend types into this file —
 * any type the backend declares inline is redeclared inline here too, for
 * the same reason. Any edit to the backend manifest must be mirrored here
 * in the same change, or the parity spec goes red.
 */

/** Mirrors `NavigationCaller` from the backend manifest — kept inline, not imported. */
export interface FeNavigationCaller {
  role: string
  tenantId: string | null | undefined
  assignedModules: readonly string[]
}

/** Mirrors `RouteEntry` from the backend manifest — kept inline, not imported. */
export interface FeRouteEntry {
  route: string
  pageKind: string
  requiredModule?: string
  requiredRole?: string
  centralOnly?: boolean
  label: string
}

export const FE_ROUTE_MANIFEST: readonly FeRouteEntry[] = [
  // --- Public, outside AuthGuard ---
  { route: '/checkout', pageKind: 'checkout', label: 'the checkout page' },
  { route: '/dashboard/view/:id', pageKind: 'dashboard-view-public', label: 'a shared dashboard' },
  { route: '/forms/view/:id', pageKind: 'form-fill-public', label: 'a public form' },

  // --- AuthGuard + AppLayout, no further gate ---
  { route: '/', pageKind: 'home', label: 'Home' },
  { route: '/map', pageKind: 'map', label: 'the map page' },
  { route: '/map/:uuid', pageKind: 'map', label: 'a saved map' },
  { route: '/library', pageKind: 'library', label: 'the library' },
  { route: '/library/:type/:id', pageKind: 'library-item', label: 'a library item' },
  { route: '/profile', pageKind: 'profile', label: 'your profile' },
  { route: '/dashboard', pageKind: 'dashboard-list', label: 'the dashboard list' },
  { route: '/dashboard/builder/:id', pageKind: 'dashboard-builder', label: 'the dashboard builder' },

  // --- Deliberately unguarded — see backend file header, item 3. NEVER add requiredModule here. ---
  { route: '/geocoding', pageKind: 'geocoding', label: 'the geocoding page' },
  { route: '/geocoding/sessions', pageKind: 'geocoding-sessions', label: 'geocoding sessions' },
  { route: '/geocoding/sessions/:id', pageKind: 'geocoding-session', label: 'a geocoding session' },
  { route: '/capacity', pageKind: 'capacity-runs', label: 'the capacity runs list' },
  { route: '/capacity/:runId', pageKind: 'capacity-run', label: 'a capacity run' },

  // --- LicenseGuard-gated ---
  {
    route: '/monitoring',
    pageKind: 'monitoring-library',
    requiredModule: 'monitoring',
    label: 'the monitoring library',
  },
  {
    route: '/monitoring/:siteId',
    pageKind: 'monitoring-site',
    requiredModule: 'monitoring',
    label: 'a monitoring site',
  },
  { route: '/forms', pageKind: 'forms-list', requiredModule: 'forms', label: 'the Forms module' },
  { route: '/forms/builder/:id', pageKind: 'forms-builder', requiredModule: 'forms', label: 'the Forms builder' },
  {
    route: '/forms/:id/submissions',
    pageKind: 'forms-submissions',
    requiredModule: 'forms',
    label: 'form submissions',
  },
  { route: '/scripts', pageKind: 'scripts-list', requiredModule: 'scripts', label: 'the Scripts module' },
  { route: '/scripts/:id', pageKind: 'script', requiredModule: 'scripts', label: 'a script' },
  { route: '/security', pageKind: 'security-dashboard', requiredModule: 'security', label: 'the Security dashboard' },
  {
    route: '/security/:type',
    pageKind: 'security-scan-type',
    requiredModule: 'security',
    label: 'a Security scan type',
  },
  // Planning (Kebenaran Merancang): every authenticated path is behind LicenseGuard ['planning']
  // (apps/console/src/routes/planning-routes.tsx) with no RoleGuard; the pages decide by GET
  // /planning/me. The public /planning/register pages are not catalogued (outside AuthGuard).
  { route: '/planning', pageKind: 'planning-home', requiredModule: 'planning', label: 'the Planning module' },
  {
    route: '/planning/submissions',
    pageKind: 'planning-submissions',
    requiredModule: 'planning',
    label: 'planning submissions',
  },
  {
    route: '/planning/submissions/new',
    pageKind: 'planning-submission-new',
    requiredModule: 'planning',
    label: 'the new planning submission form',
  },
  {
    route: '/planning/submissions/:id',
    pageKind: 'planning-submission',
    requiredModule: 'planning',
    label: 'a planning submission',
  },
  {
    route: '/planning/registrations',
    pageKind: 'planning-registrations',
    requiredModule: 'planning',
    label: 'the planning applicant registrations',
  },
  {
    route: '/planning/activity',
    pageKind: 'planning-activity',
    requiredModule: 'planning',
    label: 'planning activity',
  },

  // --- RoleGuard-gated (admin) ---
  { route: '/settings', pageKind: 'settings', requiredRole: 'admin', label: 'Settings' },

  // --- /settings/* sub-sections (apps/console/src/pages/settings/Settings.tsx).
  // These inherit the top-level RoleGuard(admin) above and add no further
  // route-tree gate of their own — the sidebar's `requiresModule`/
  // `requiresRole`/`cloudOnly` filters in `settings-nav.ts` are UI-only nav
  // hiding, NOT enforced by a route guard, so they are deliberately not
  // modeled here (would silently over-refuse a reachable page). ---
  { route: '/settings/map', pageKind: 'settings-map', requiredRole: 'admin', label: 'Map settings' },
  { route: '/settings/branding', pageKind: 'settings-branding', requiredRole: 'admin', label: 'Branding settings' },
  { route: '/settings/license', pageKind: 'settings-license', requiredRole: 'admin', label: 'License settings' },
  {
    route: '/settings/utilities',
    pageKind: 'settings-utilities',
    requiredRole: 'admin',
    label: 'Utilities settings',
  },
  { route: '/settings/users', pageKind: 'settings-users', requiredRole: 'admin', label: 'user settings' },
  { route: '/settings/servers', pageKind: 'settings-servers', requiredRole: 'admin', label: 'Servers settings' },
  {
    route: '/settings/menu-cards',
    pageKind: 'settings-menu-cards',
    requiredRole: 'admin',
    label: 'Menu Cards settings',
  },
  { route: '/settings/groups', pageKind: 'settings-groups', requiredRole: 'admin', label: 'Groups settings' },
  { route: '/settings/devices', pageKind: 'settings-devices', requiredRole: 'admin', label: 'Devices settings' },
  { route: '/settings/usage', pageKind: 'settings-usage', requiredRole: 'admin', label: 'Usage settings' },
  { route: '/settings/billing', pageKind: 'settings-billing', requiredRole: 'admin', label: 'Billing settings' },
  { route: '/settings/security', pageKind: 'settings-security', requiredRole: 'admin', label: 'Security settings' },
  {
    route: '/settings/geocoding-cache',
    pageKind: 'settings-geocoding-cache',
    requiredRole: 'admin',
    label: 'Geocoding Cache settings',
  },
  {
    route: '/settings/controller',
    pageKind: 'settings-controller',
    requiredRole: 'admin',
    label: 'Controller settings',
  },

  // --- /settings/planning: Settings.tsx wraps it in LicenseGuard ['planning'] on
  // top of the /settings/* RoleGuard(admin), so both gates are modeled. ---
  {
    route: '/settings/planning',
    pageKind: 'settings-planning',
    requiredRole: 'admin',
    requiredModule: 'planning',
    label: 'Planning settings',
  },

  // --- /settings/* sub-sections wrapped in `RequireCentralAdmin` — the ONE
  // real per-section route guard in Settings.tsx (see `centralOnly` doc in the backend file). ---
  {
    route: '/settings/tenants',
    pageKind: 'settings-tenants',
    requiredRole: 'admin',
    requiredModule: 'multitenancy',
    centralOnly: true,
    label: 'Tenants settings',
  },
  {
    route: '/settings/audit-logs',
    pageKind: 'settings-audit-logs',
    requiredRole: 'admin',
    requiredModule: 'multitenancy',
    centralOnly: true,
    label: 'Audit Logs settings',
  },
  {
    route: '/settings/jobs',
    pageKind: 'settings-jobs',
    requiredRole: 'admin',
    requiredModule: 'multitenancy',
    centralOnly: true,
    label: 'Jobs settings',
  },
]

/**
 * Compact `route — label` listing, mirroring the backend's
 * `KNOWN_ROUTES_LISTING` (`services/console/ai/src/routes/routeManifest.ts`)
 * byte-for-byte — same reason the whole manifest above is a copy, not an
 * import: `apps/console` keeps this dependency-free.
 */
export const FE_KNOWN_ROUTES_LISTING: string = FE_ROUTE_MANIFEST.map(e => `${e.route} — ${e.label}`).join('\n')
